import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  GlobalStyles,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { styled } from "@mui/system";
import singup from "../assets/singup-img.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import backIcon from "../assets/back-icon.png";
import { API_BASE_URL } from "../apiConfig";
import AppDialog from "../components/AppDialog";
import { useLoading } from "../context/LoadingContext";

// -------- Frame with MOBILE ONLY adjustments --------
const Frame = styled(Box)(({ theme }) => ({
  maxWidth: "900px",
  width: "100%",
  minHeight: "400px",
  margin: "auto",
  position: "relative",
  padding: "40px",
  borderRadius: "20px",
  background: `repeating-linear-gradient(
    to right,
    #1a101c 0px,
    #1a101c 4px,
    #120a13 4px,
    #120a13 6px
  )`,
  // MOBILE ONLY
  [theme.breakpoints.down("sm")]: {
    maxWidth: "80%", // narrower on mobile
    minHeight: "500px", // taller on mobile
    padding: "20px",
    overflow: "hidden",
    background: `url(${singup}) no-repeat center bottom`,
    backgroundSize: "contain",
  },
}));

const SvgBorder = styled("svg")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
});

const FixedLabelTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    background: "rgba(0,0,0,0.3)",
    backdropFilter: "blur(100px)",
    color: "#8E8D97",
    borderRadius: 8,
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
      fontSize: "16px",
      color: "white",
    },
    "& input::placeholder": {
      color: "rgba(255,255,255,0.7)",
      opacity: 1,
    },
  },
});

const borderPath = `
M 24 0
H 650
L 665 40
H 770
L 785 0
H 826
A 24 24 0 0 1 850 24
V 426
A 24 24 0 0 1 826 450
H 360
L 320 410
H 130
L 90 450
H 24
A 24 24 0 0 1 0 426
V 24
A 24 24 0 0 1 24 0
Z
`;

const boldPath = `
  M215 0 H650 
  M785 0 H826         
  A24 24 0 0 1 850 24
  V426
  A24 24 0 0 1 826 450
  M826 450 H360
  M90 450 H24
  A24 24 0 0 1 0 426
  V24
  A24 24 0 0 1 24 0
  M24 0 H215
  Z
`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [dialog, setDialog] = useState({ open: false, type: "success", message: "", navigateOnClose: false });

  const [showPassword, setShowPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);

  const showDialog = (type, message, navigateOnClose = false) =>
    setDialog({ open: true, type, message, navigateOnClose });

  const closeDialog = () => {
    const shouldNav = dialog.navigateOnClose;
    setDialog((d) => ({ ...d, open: false }));
    if (shouldNav) navigate("/sign-in");
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = async () => {
    if (formData.password !== formData.confirmPassword) {
      showDialog("error", "Passwords do not match!");
      return;
    }

    const reset_token = sessionStorage.getItem("reset_token");
    const email = sessionStorage.getItem("reset_email");

    if (!reset_token || !email) {
      showDialog("error", "Missing reset token. Please restart the reset flow.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/reset-password`, {
        email,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        reset_token,
      });

      const token = res.data.token;
      localStorage.setItem("authToken", token);
      sessionStorage.removeItem("reset_token");
      sessionStorage.removeItem("reset_email");

      showDialog("success", "Your password has been reset successfully!", true);
    } catch (err) {
      showDialog("error", err.response?.data?.message || "Reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "@font-face": {
            fontFamily: "BRUSHSTRIKE",
            src: `url("/fonts/Brushstrike.ttf") format("truetype")`,
          },
        }}
      />

      <Box
        sx={{
          height: "100%",
          minHeight: "100vh",
          pt: 5,
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#0A0D17",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "20%",
            width: { xs: "500px", sm: "650px", md: "800px" },
            height: { xs: "500px", sm: "650px", md: "800px" },
            background:
              "radial-gradient(circle, rgba(51, 178, 247, 0.73), rgba(84, 14, 92, 0.6), transparent 50%)",
            transform: "translate(-50%, -50%)",
            filter: "blur(120px)",
            zIndex: 0,
          },
        }}
      >
        {/* Title */}
        <Box sx={{ width: "100%", textAlign: "center", mb: 2 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 400,
              fontFamily: "BRUSHSTRIKE",
              letterSpacing: "0.03em",
              backgroundImage:
                "linear-gradient(90deg, #CF36E1 0%, #D100FF 35%, #33B2F7 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
              fontSize: { xs: "32px", sm: "40px", md: "50px" },
            }}
          >
            Password Reset
          </Typography>
        </Box>

        <Frame>
          <SvgBorder viewBox="0 0 850 450" preserveAspectRatio="none">
            <path d={borderPath} stroke="#ff00ff" strokeWidth="2" fill="none" />
            <path d={boldPath} stroke="#ff00ff" strokeWidth="10" fill="none" />
          </SvgBorder>

          {/* Bottom Button */}
          <Box
            onClick={handleReset}
            sx={{
              position: "absolute",
              bottom: -8,
              left: { xs: 10, sm: 20, md: 115 },
              width: 300,
              height: 70,
              zIndex: 5,
              cursor: "pointer",
            }}
          >
            <SvgBorder
              width="100%"
              height="100%"
              viewBox="0 0 300 70"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="resetGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#B210C1" />
                  <stop offset="100%" stopColor="#0F0F18" />
                </linearGradient>
              </defs>
              <path
                d="M10 60 L40 30 L250 30 L280 60 Z"
                fill="url(#resetGradient)"
                stroke="#ff00ff"
                strokeWidth="3"
                style={{ cursor: "pointer" }}
              />
              <text
                x="150"
                y="45"
                fill="#FFFFFF"
                fontSize="18"
                fontWeight="600"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                Update Password
              </text>
            </SvgBorder>
          </Box>

          {/* Form */}
          <Box
            sx={{
              position: "relative",
              height: "100%",
              display: "flex",
              justifyContent: { xs: "center", md: "flex-start" },
              alignItems: "center",
              pl: { xs: 0, sm: 2, md: 5 },
              pr: { xs: 0, sm: 2, md: 0 },
            }}
          >
            <Box
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "90%", sm: "80%", md: "550px" },
                gap: 2,
                zIndex: 2,
                p: { xs: 2, sm: 3 },
              }}
            >
              {/* Back + Title */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, cursor: "pointer", mb: 0.5 }}>
                <Box
                  onClick={() => navigate("/sign-in")}
                  component="img"
                  src={backIcon}
                  alt="back-icon"
                  sx={{ width: 22 }}
                />
                <Typography sx={{ color: "white", fontSize: "32px", fontWeight: 700 }}>
                  Reset Password
                </Typography>
              </Box>

              {/* Password */}
              <Box>
                <Typography sx={{ color: "white", mb: 0.5, fontSize: "14px" }}>Password</Typography>
                <FixedLabelTextField
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  fullWidth
                  value={formData.password}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          sx={{ color: "#868A93" }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Confirm Password */}
              <Box>
                <Typography sx={{ color: "white", mb: 0.5, fontSize: "14px" }}>Confirm Password</Typography>
                <FixedLabelTextField
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  fullWidth
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          sx={{ color: "#868A93" }}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Frame>

        {/* USER IMAGE BOTTOM - MOBILE ADJUSTED */}
        <Box
          component="img"
          src={singup}
          alt="Character"
          sx={{
            position: "absolute",
            bottom: { xs: -10, sm: -20 },
            right: { xs: "50%", sm: 20 },
            transform: { xs: "translateX(-50%) scaleX(-1)", sm: "scaleX(-1)" },
            width: { xs: 0, sm: 280, md: 380, lg: 430 }, // hidden on mobile
            opacity: { xs: 0, sm: 1 },
            objectFit: "contain",
            zIndex: 1,
          }}
        />
      </Box>

      <AppDialog
        open={dialog.open}
        onClose={closeDialog}
        type={dialog.type}
        message={dialog.message}
      />
    </>
  );
};

export default ResetPassword;
